import { Box3, BoxGeometry, DoubleSide, Group, Mesh, MeshBasicMaterial, type MeshStandardMaterial, type Object3D, PointLight, Quaternion, Vector3 } from 'three';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import { createCamera, createRenderer, createScene } from './scene/sceneGraph';
import type { CameraMode } from './camera/cameraMode';
import { FreeformMode } from './camera/freeformMode';
import { LockedFreeformMode } from './camera/lockedFreeformMode';
import { ExternalViewMode } from './camera/externalViewMode';
import type { FreeformInitOptions } from './camera/freeformMode';
import { RailsMode } from './camera/railsMode';
import { TweenCameraMode } from './camera/tweenMode';
import { StateController } from './state/stateController';
import { InstantSwap } from './transitions/instantSwap';
import { OpacityCrossfade } from './transitions/opacityCrossfade';
import type { Transition } from './transitions/transition';
import { GLBLoader } from './loaders/glbLoader';
import { HeroLoader } from './loaders/heroLoader';
import { PointerInteraction } from './interaction/pointer';
import { InteractionEngine } from './interaction/engine';
import { RULES } from './interaction/rules';
import { AudioManager } from './audio/audioManager';
import { loadAudioManifest } from './audio/manifest';
import { loadAudioCues } from './audio/cues';
import { SoundscapeEngine } from './audio/soundscapeEngine';
import { placementsFromStateConfig } from './audio/soundscape';
import { loadSoundscape } from './audio/soundscapeStore';
import { getHeroId, isInteractive, tagInteractive, buildHeroDropdownMap } from './scene/tagging';
import { devBus } from './debug/devBus';
import { hotkeyRegistry } from './debug/hotkeyRegistry';
import { isTextEntryTarget } from './debug/keyboardTarget';
import { DebugVizScene } from './debug/dvScene';
import { AudioGizmos } from './debug/audioGizmos';
import { ClothGizmos } from './debug/clothGizmos';
import { SceneGizmos } from './debug/sceneGizmos';
import { createTimeline } from './debug/timeline';
import { createInspector } from './debug/inspector';
import { createEventLog } from './debug/eventLog';
import { createHotkeyHint } from './debug/hotkeyHint';
import { initDirectorMode } from './debug/directorMode';
import type { Atmosphere } from './atmosphere/atmosphere';
import { NoAtmosphere } from './atmosphere/atmosphere';
import { MorningShaft, type MorningShaftConfig, type ShaftDef } from './atmosphere/morningShaft';
import { TimeOfDayClock } from './atmosphere/timeOfDayClock';
import { SunRig } from './atmosphere/sunRig';
import { CloudSky } from './atmosphere/cloudSky';
import { loadStateConfig } from './state/stateConfig';
import { createWallCull, type CullSettings } from './scene/wallCull';
import { createCameraHandles } from './scene/cameraHandles';
import { createSaveFlows, applyCameraTunables } from './persist/saveFlows';
import { createSaveHub } from './persist/saveHub';
import { createGizmoUndo } from './scene/gizmoUndo';
import { createPowerLatch } from './state/power';
import { createAudioControls } from './ui/audioControls';
import { createLogoBadge } from './ui/logoBadge';
import { HoverHighlight } from './scene/hoverHighlight';
import { ClothPatch } from './scene/clothPatch';
import { ClothGrabController } from './interaction/clothGrab';
import { createHoverLabel } from './ui/hoverLabel';
import { createViewToggle } from './ui/viewToggle';
import { createDevToggle } from './ui/devToggle';
import { createControlsHint } from './ui/controlsHint';
import { createDemoChip } from './ui/demoChip';
import { createDemoTimeline, type DemoTimeline } from './ui/demoTimeline';
import { createBottomContext, type BottomContext } from './ui/bottomContext';
import { createGetOutPill, type GetOutPill } from './ui/getOutPill';
import { createYouAreHere, type YouAreHere } from './ui/youAreHere';
import { createStoryCard } from './ui/storyCard';
import { createStoryDirector, type StoryDirector } from './ui/storyDirector';
import { createCameraModeToggle, type CameraModeToggle } from './ui/cameraModeToggle';

// A bookmark is a camera pose (+ optionally the time-of-day it was captured at).
// DM13 (REDESIGNED): bookmarks TRANSCEND eras — the `morph` field is gone, so a
// bookmark never carries or snaps the era (which deletes the era-snap bug
// outright). A bookmark may instead be ANCHORED to a hero: its position/target
// are then stored in that hero's LOCAL space, so it still frames the hero after
// the hero is re-placed. Non-anchored bookmarks store world-space pose as before.
interface CameraBookmark {
  position: [number, number, number];
  target: [number, number, number];
  /** Time of day 0..1 when captured (era is NOT carried — DM13). */
  t?: number;
  /** DM13 — hero this bookmark frames; when set, position/target are hero-local. */
  anchor?: string;
  /** narration-MVP — a soundscape clip id to fire (soundscape.fireClipExternally)
   *  when the camera ARRIVES here. The auto/interactive duality: the timeline may
   *  also schedule the same clip, but whoever fires first latches it for the pass
   *  so it never doubles. The chair → cassettes narration is the first exemplar. */
  onArrive?: { play?: string };
}

export async function start(container: HTMLElement): Promise<void> {
  // `?dev=1` (or `?dev`) unlocks the authoring surfaces: the lil-gui dev
  // panel, the per-hero state radio panel, and the green/cyan camera
  // anchor markers. Default load is the polished demo view.
  const devMode = new URLSearchParams(window.location.search).has('dev');

  const scene = createScene();
  const camera = createCamera(window.innerWidth, window.innerHeight);
  const renderer = createRenderer();
  renderer.setSize(window.innerWidth, window.innerHeight);
  // Per-material clipping planes — the wall cull uses one to cut just the
  // fragments between camera and room, not the whole occluding mesh.
  renderer.localClippingEnabled = true;
  container.appendChild(renderer.domElement);

  // ── Camera config load ───────────────────────────────────────────────
  // Defaults are the backup if positions.json is missing/malformed.
  const exteriorPos = new Vector3(12, 5, 8);
  const exteriorTarget = new Vector3(2.0, 1.5, -1.6);
  const doorway = new Vector3(0.5, 1.3, 2.0);
  const interiorAABB = new Box3(
    new Vector3(-2.75, -0.5, -3.2),
    new Vector3(1.0, 3.5, 2.5),
  );
  const cullSettings: CullSettings = { offset: 0, enabled: true };
  let cameraTunablesFromDisk: Record<string, Record<string, number>> | null = null;
  // Bookmarks: named camera poses surfaced in the dev panel. Persisted
  // alongside the rest of positions.json. Empty map until the user saves one.
  const bookmarks: Record<string, CameraBookmark> = {};
  try {
    const camRes = await fetch('/camera/positions.json', { cache: 'no-store' });
    if (camRes.ok) {
      const data = (await camRes.json()) as {
        exterior?: { position?: number[]; target?: number[] };
        doorway?: number[];
        interiorAABB?: { min?: number[]; max?: number[] };
        cull?: { offset?: number; enabled?: boolean };
        tunables?: Record<string, Record<string, number>>;
        bookmarks?: Record<string, { position: number[]; target: number[]; t?: number; morph?: number; anchor?: string; onArrive?: { play?: string } }>;
      };
      const p = data.exterior?.position;
      const t = data.exterior?.target;
      if (Array.isArray(p) && p.length === 3) exteriorPos.set(p[0], p[1], p[2]);
      if (Array.isArray(t) && t.length === 3) exteriorTarget.set(t[0], t[1], t[2]);
      if (Array.isArray(data.doorway) && data.doorway.length === 3) {
        doorway.set(data.doorway[0], data.doorway[1], data.doorway[2]);
      }
      const aMin = data.interiorAABB?.min;
      const aMax = data.interiorAABB?.max;
      if (Array.isArray(aMin) && aMin.length === 3) interiorAABB.min.set(aMin[0], aMin[1], aMin[2]);
      if (Array.isArray(aMax) && aMax.length === 3) interiorAABB.max.set(aMax[0], aMax[1], aMax[2]);
      if (typeof data.cull?.offset === 'number') cullSettings.offset = data.cull.offset;
      if (typeof data.cull?.enabled === 'boolean') cullSettings.enabled = data.cull.enabled;
      if (data.tunables) cameraTunablesFromDisk = data.tunables;
      if (data.bookmarks) {
        for (const [name, b] of Object.entries(data.bookmarks)) {
          if (Array.isArray(b.position) && b.position.length === 3 &&
              Array.isArray(b.target) && b.target.length === 3) {
            bookmarks[name] = {
              position: [b.position[0], b.position[1], b.position[2]],
              target: [b.target[0], b.target[1], b.target[2]],
              ...(typeof b.t === 'number' ? { t: b.t } : {}),
              // DM13 — `morph` is intentionally dropped on load: bookmarks no
              // longer carry the era (a save then strips it from disk). `anchor`
              // (hero-local framing) is the new optional field.
              ...(typeof b.anchor === 'string' ? { anchor: b.anchor } : {}),
              // narration-MVP — optional fire-on-arrival soundscape clip id.
              ...(b.onArrive && typeof b.onArrive.play === 'string' ? { onArrive: { play: b.onArrive.play } } : {}),
            };
          }
        }
      }
    }
  } catch (e) {
    console.warn('[camera] positions.json load failed, using defaults', e);
  }
  // The chair sit is intentionally AMBIENT (John, 2026-06-29): the why-sudanese
  // narration used to fire on arrival here, but it crowded the cassettes too early.
  // Removed — the chair is a quiet seated beat now; Annour's lines live on the
  // cassette-wall click. (Hence NO onArrive re-assertion + none in the bookmark.)
  camera.position.copy(exteriorPos);
  camera.lookAt(exteriorTarget);
  // Boot-time diagnostic so we can confirm positions.json actually applied.
  console.log('[camera] applied exterior pose:',
    `pos=[${camera.position.toArray().map((v) => v.toFixed(2)).join(', ')}]`,
    `target=[${exteriorTarget.toArray().map((v) => v.toFixed(2)).join(', ')}]`,
    `doorway=[${doorway.toArray().map((v) => v.toFixed(2)).join(', ')}]`,
  );

  // Interior path lives inside SolidWallStructure's footprint (3.7×5.4m,
  // centered ~(-0.9, _, -0.5)). Tightened to 0.85m inset and routed to pass
  // near each hero's placement so the walk reads as a tour.
  const interiorCenter = new Vector3(-0.9, 1.0, -0.5);
  const interiorPath: Vector3[] = [
    new Vector3( 0.1, 1.0,  1.2),  // front-right (near entrance)
    new Vector3(-1.0, 1.0,  0.8),  // moving toward speaker
    new Vector3(-1.5, 1.0, -0.5),  // mid-room
    new Vector3(-1.0, 1.0, -1.5),  // mid-back
    new Vector3( 0.3, 1.0, -1.8),  // near boombox
    new Vector3( 0.3, 1.0,  0.0),  // right side returning
  ];

  const cameras: Record<string, CameraMode> = {
    'freeform':       new FreeformMode(camera, renderer.domElement, exteriorTarget),
    // E1 — beat-1 establishing shot: a constrained neck-turn from the boot
    // exterior standpoint (look around a little; the only way in is the door).
    'external-view':  new ExternalViewMode(camera, renderer.domElement, exteriorTarget),
    'interior-walk':  new RailsMode(camera, renderer.domElement, {
      path: interiorPath,
      lookAt: 'ahead',
      closed: true,
      clothGrabbing: () => clothGrab.isGrabbing(), // A1 — cloth-grab keeps pointer precedence
    }),
    'interior-orbit': new RailsMode(camera, renderer.domElement, {
      path: interiorPath,
      lookAt: interiorCenter,
      closed: true,
      clothGrabbing: () => clothGrab.isGrabbing(), // A1 — cloth-grab keeps pointer precedence
    }),
    // A2/A3 — the seated "sit in the chair" camera: constrained orbit (pan off,
    // wide look cone, subtle lean band) pivoting on the table. goToBookmark('chair')
    // hands off to it (seeded with the chair target) instead of wide-open freeform.
    'locked-freeform': new LockedFreeformMode(camera, renderer.domElement, interiorCenter),
  };
  if (cameraTunablesFromDisk) {
    for (const [name, cam] of Object.entries(cameras)) {
      const saved = cameraTunablesFromDisk[name];
      if (saved) applyCameraTunables(cam, saved);
    }
  }

  // E1 — the player opens on the constrained establishing shot (beat 1); a
  // ?dev=1 authoring session boots into freeform so John can navigate freely.
  const initialCameraName = devMode ? 'freeform' : 'external-view';
  let activeCamera: CameraMode = cameras[initialCameraName];
  activeCamera.init();
  // Post-init log: OrbitControls.update() runs during init() and shouldn't
  // change camera.position when the spherical round-trip is lossless. If
  // this value drifts from the "applied exterior pose" log above, something
  // is moving the camera between those two points.
  console.log('[camera] post-init pose:',
    `pos=[${camera.position.toArray().map((v) => v.toFixed(3)).join(', ')}]`,
  );

  const setCamera = (name: string, entryTarget?: Vector3): void => {
    const next = cameras[name];
    if (!next || next === activeCamera) return;
    activeCamera.dispose();
    activeCamera = next;
    // B3 — pass the entry target so freeform seeds its orbit pivot there and
    // its first update() is a no-op. Without it freeform.init() resets the
    // pivot to its constructor default (exteriorTarget), snapping the look
    // direction off a just-restored bookmark. Modes that don't orbit ignore it.
    activeCamera.init(entryTarget ? { target: entryTarget } : undefined);
  };
  const getActiveCamera = (): CameraMode => activeCamera;

  // currentTarget(): a Vector3 representing what the camera is currently
  // pointed at. For freeform that's controls.target; for any other mode we
  // synthesize a point on the forward ray so a tween's rotation lerp
  // matches what's currently on screen.
  const currentTarget = (): Vector3 => {
    const t = new Vector3();
    const cur = activeCamera as unknown as { controls?: { target: Vector3 } };
    if (cur.controls) {
      t.copy(cur.controls.target);
    } else {
      const fwd = new Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
      t.copy(camera.position).add(fwd.multiplyScalar(5));
    }
    return t;
  };

  // ── View state (outside/inside) + tween helpers ───────────────────────
  let view: 'exterior' | 'interior' = 'exterior';
  const VIEW_TWEEN_DURATION = 1.6;

  // Late-bound: assigned once gizmos + atmosphere exist. The initial no-op
  // covers any pre-setup invocation.
  let detachGizmos: () => void = () => { /* set later */ };

  /** Cross-fades the camera from current pose to (toPos, toTarget). Hands
   *  off to `destCamera` after the tween. Optionally routes through a
   *  waypoint (used by setView to clear the doorway without clipping).
   *
   *  Waypoint sanity check: if the proposed waypoint isn't on the path
   *  between fromPos and toPos — measured by projecting it onto the
   *  from→to line — we drop it and tween straight. Without this, a
   *  doorway saved behind the exterior pose (e.g. further east than the
   *  outside camera) creates a Catmull-Rom curve that lurches east first
   *  then U-turns west into the interior. Better to go direct than to
   *  detour through an off-path waypoint. */
  const tweenToPose = (
    toPos: Vector3,
    toTarget: Vector3,
    destCamera: string,
    waypoint?: Vector3,
    onArrive?: () => void,
  ): void => {
    detachGizmos();
    const fromPos = camera.position.clone();
    const fromTarget = currentTarget();

    let effectiveWaypoint = waypoint;
    if (waypoint) {
      const dir = new Vector3().subVectors(toPos, fromPos);
      const lenSq = dir.lengthSq();
      if (lenSq < 1e-6) {
        effectiveWaypoint = undefined;
      } else {
        const fromW = new Vector3().subVectors(waypoint, fromPos);
        const t = fromW.dot(dir) / lenSq;
        // [0.05, 0.95] = waypoint is meaningfully between endpoints. Outside
        // that range it'd just be a detour. The tight inner bound also avoids
        // jitter when the camera is already sitting on the waypoint.
        if (t < 0.05 || t > 0.95) {
          console.warn(
            `[camera] doorway at (${waypoint.x.toFixed(2)}, ${waypoint.y.toFixed(2)}, ${waypoint.z.toFixed(2)}) ` +
            `isn't on the path from (${fromPos.x.toFixed(2)}, ${fromPos.y.toFixed(2)}, ${fromPos.z.toFixed(2)}) → ` +
            `(${toPos.x.toFixed(2)}, ${toPos.y.toFixed(2)}, ${toPos.z.toFixed(2)}) — ` +
            `t=${t.toFixed(2)}. Skipping waypoint; tweening straight. ` +
            `Drag the cyan doorway marker onto the actual door opening to use it as a waypoint.`,
          );
          // DM13 — surface the misconfig IN-UI (the event log), not just console.
          devBus.emit('camera:notice', { message: 'doorway marker not on the entry path — tweened straight; drag the cyan marker onto the door' });
          effectiveWaypoint = undefined;
        }
      }
    }

    activeCamera.dispose();
    activeCamera = new TweenCameraMode(
      camera,
      { position: fromPos, target: fromTarget },
      { position: toPos.clone(), target: toTarget.clone() },
      VIEW_TWEEN_DURATION,
      // The hand-off runs inside the tween's update() → render-loop tick(). B3:
      // seed the dest with the tween's FINAL target so freeform (and any orbiting
      // dest) preserves the arrived pose instead of snapping to default.
      // DM3 — if the hand-off throws, the exception would escape tick() and the
      // render loop would never re-schedule (rAF) — a TOTAL freeze, not just the
      // camera. Wrap it: on any throw, force-recover to freeform (re-init even if
      // activeCamera already points there — a half-failed setCamera can leave it
      // assigned-but-uninitialised), and swallow a second failure so the loop
      // always survives. The camera ends live + controllable, never stuck.
      () => {
        try {
          setCamera(destCamera, toTarget);
        } catch (e) {
          console.error('[camera] hand-off to', destCamera, 'threw — recovering to freeform', e);
          try {
            activeCamera.dispose();
            activeCamera = cameras['freeform'];
            activeCamera.init({ target: toTarget });
          } catch (e2) {
            console.error('[camera] freeform recovery also threw; reload to recover', e2);
          }
        }
        // narration-MVP — fire-on-arrival hook (DM13 onArrive). Runs AFTER the
        // camera hand-off, in its OWN try/catch so a soundscape error can never
        // trip the camera-recovery path above (DM3 — the loop must always survive).
        if (onArrive) {
          try { onArrive(); }
          catch (e) { console.error('[camera] onArrive hook threw', e); }
        }
      },
      effectiveWaypoint,
    );
    activeCamera.init();
  };

  const setView = (v: 'exterior' | 'interior'): void => {
    view = v;
    // E2 — the doorway tween sweeps the ambient low-pass: interior tone OPENS as
    // you enter, muffles (through the wall) as you step out. Rides the same
    // ~0.6s as the camera move. (Cutoff range + muffle feel: John's ear.)
    audio.setEntryOpenness(v === 'interior' ? 1 : 0);
    const toPos = v === 'exterior' ? exteriorPos : interiorPath[0];
    const toTarget = v === 'exterior' ? exteriorTarget : interiorCenter;
    const destName = v === 'exterior' ? 'freeform' : 'interior-orbit';
    // Entering the interior: reset both rails cameras to t=0 so the rails
    // pick up at the entrance (where the tween hands off) instead of
    // snapping back to wherever the user had scrolled last session.
    if (v === 'interior') {
      (cameras['interior-walk'] as RailsMode).t = 0;
      (cameras['interior-orbit'] as RailsMode).t = 0;
    }
    // Always route through the doorway — the camera tween otherwise cuts
    // through walls on the way in or out.
    tweenToPose(toPos, toTarget, destName, doorway);
  };
  const getView = (): 'exterior' | 'interior' => view;

  // A4 — leave the seated chair (LockedFreeform) and tween back onto the interior
  // orbit rail. Reset the rail to its entrance so the tween's destination matches
  // where interior-orbit hands off (no snap), mirroring setView('interior').
  const getOutOfChair = (): void => {
    (cameras['interior-orbit'] as RailsMode).t = 0;
    tweenToPose(interiorPath[0], interiorCenter, 'interior-orbit');
  };

  /** Tween to a bookmark. Auto-routes through the doorway if the bookmark
   *  is on the opposite side of the wall, and updates the view flag so
   *  wall cull behaves correctly post-landing. */
  const goToBookmark = (name: string, opts?: { handoff?: string }): void => {
    const b = bookmarks[name];
    if (!b) return;
    const toPos = new Vector3(b.position[0], b.position[1], b.position[2]);
    const toTarget = new Vector3(b.target[0], b.target[1], b.target[2]);
    // DM13 — a hero-anchored bookmark stores its pose in the hero's LOCAL space;
    // recompute world pose from the hero's CURRENT transform so it still frames
    // the hero after it's been re-placed. If the anchor hero is gone, fall back
    // to treating the stored pose as world + surface a notice.
    if (b.anchor) {
      const hero = heroLookup.get(b.anchor);
      if (hero) {
        hero.updateWorldMatrix(true, false);
        toPos.applyMatrix4(hero.matrixWorld);
        toTarget.applyMatrix4(hero.matrixWorld);
      } else {
        devBus.emit('camera:notice', { message: `bookmark "${name}" anchor '${b.anchor}' is gone — framing may be off` });
      }
    }
    const destIsInside = interiorAABB.containsPoint(toPos);
    const fromIsInside = interiorAABB.containsPoint(camera.position);
    view = destIsInside ? 'interior' : 'exterior';
    audio.setEntryOpenness(destIsInside ? 1 : 0); // E2 — match the entry low-pass to where the bookmark lands
    // DM13 — reset the interior rails on an inside landing (mirrors setView) so a
    // later switch to a rails camera doesn't teleport from a stale rail position.
    if (destIsInside) {
      (cameras['interior-walk'] as RailsMode).t = 0;
      (cameras['interior-orbit'] as RailsMode).t = 0;
    }
    // Bookmarks land in freeform by default (user-curated single poses, not loops
    // to ride). A2/A3 — a caller can override the hand-off (the chair sit hands
    // off to 'locked-freeform' so you're seated facing the table, not free-flying).
    // narration-MVP — fire-on-arrival: when the bookmark carries an onArrive
    // clip, hand the tween a thunk that fires it via the soundscape's external
    // path on landing. fireClipExternally latches against the timeline's own
    // crossing so the line never doubles (the chair → cassettes exemplar).
    const arrive = b.onArrive?.play ? () => soundscape.fireClipExternally(b.onArrive!.play!) : undefined;
    tweenToPose(toPos, toTarget, opts?.handoff ?? 'freeform', destIsInside !== fromIsInside ? doorway : undefined, arrive);
    // Bookmarks KEEP the current time of day (John, 2026-06-29): snapping the
    // clock to the captured frame read as incongruous — sitting in the chair at a
    // different time than now. The ERA likewise doesn't change (DM13 — bookmarks
    // transcend eras), so a bookmark is a pure camera move. (`b.t` is still parsed
    // on load for back-compat but no longer applied.)
    devBus.emit('bookmark:tween', { name });
  };

  // ── Content load ─────────────────────────────────────────────────────
  // Pointer raycasts against contentRoot — scoping it tight keeps gizmo
  // helpers, lights, and the camera-handle markers out of the hit list.
  const contentRoot = new Group();
  scene.add(contentRoot);

  const heroLookup = new Map<string, Object3D>();
  const indexHeroes = (objects: Object3D[]): void => {
    for (const root of objects) {
      root.traverse((obj) => {
        const id = getHeroId(obj);
        if (id) heroLookup.set(id, obj);
      });
    }
  };

  // Item 8 — resolve a BARE hero id to its era-VISIBLE instance for a
  // multi-placement hero. The loader keys each placement `id#i` and wraps them
  // in an origin-pinned `id#all` group (there is NO bare `id` entry for a multi
  // hero — singletons keep their bare `id`). An audio / interaction anchor like
  // soundscape `at.heroId:'hero_boombox'` must follow the instance that's
  // actually on-screen this era; otherwise a per-era boombox resolves to the
  // origin group and its music detaches to room-center. The crossfade toggles
  // each placement's mesh `visible` per era, so the on-era instance is the one
  // with a visible mesh. Singletons and explicit `#i`/`#all`/`group:` asks pass
  // straight through (heroLookup hit) — zero change for every current hero.
  const hasVisibleMesh = (obj: Object3D): boolean => {
    let found = false;
    obj.traverse((n) => { if (!found && (n as { isMesh?: boolean }).isMesh && n.visible) found = true; });
    return found;
  };
  const resolveHeroObject = (id: string): Object3D | undefined => {
    const direct = heroLookup.get(id);
    if (direct) return direct;                   // singleton, or an explicit instance/group id
    const group = heroLookup.get(id + '#all');   // multi-placement: bare id absent → the wrapper
    if (!group) return undefined;
    let first: Object3D | undefined;
    for (const child of group.children) {
      const cid = getHeroId(child);
      if (!cid || cid.endsWith('#all')) continue; // only the `id#i` placement instances
      first ??= child;
      if (hasVisibleMesh(child)) return child;    // the era-visible one
    }
    return first ?? group;                        // none drawn yet (pre-first-crossfade) → first instance
  };

  const glb = new GLBLoader(renderer);

  // Defensive: glTF materials with alphaMode=BLEND import as transparent
  // and depthWrite=false. Both break opaque hero rendering — reset them.
  const forceOpaqueMaterials = (root: Object3D): void => {
    root.traverse((obj) => {
      const m = (obj as Mesh).material;
      if (!m) return;
      const list = Array.isArray(m) ? m : [m];
      for (const mat of list) {
        const ms = mat as MeshStandardMaterial & {
          transparent: boolean;
          depthWrite: boolean;
        };
        if (ms.transparent || !ms.depthWrite) {
          ms.transparent = false;
          ms.depthWrite = true;
          ms.needsUpdate = true;
        }
      }
    });
  };

  // Hero-only: force every material to render both sides. Blender exports
  // `doubleSided: false` for any material with backface-culling enabled, and
  // imported props frequently have a few inverted normals from their source —
  // the combination makes front faces silently invisible (you see through
  // the prop to its inside). DoubleSide eliminates the dependency on
  // per-mesh normal correctness, which is the kind of data hygiene we don't
  // want to enforce per export. Not applied to shared.glb because walls
  // SHOULD stay single-sided (you don't want to see interior textures from
  // outside).
  const forceDoubleSidedMaterials = (root: Object3D): void => {
    root.traverse((obj) => {
      const m = (obj as Mesh).material;
      if (!m) return;
      const list = Array.isArray(m) ? m : [m];
      for (const mat of list) {
        // Respect a deliberately alpha-MASKED cutout's authored sidedness
        // (hero_calendar's single-sided image-plane: alphaMode MASK → alphaTest
        // 0.5). Force-double-siding it would override the FrontSide it ships with,
        // so a mis-faced plane renders the texture mirrored from behind instead of
        // cleanly hiding — which defeats "rotate it to face the room." Solid props
        // (no alphaTest) still get the inverted-normal safety net.
        if (mat.alphaTest > 0) continue;
        if (mat.side !== DoubleSide) {
          mat.side = DoubleSide;
          mat.needsUpdate = true;
        }
      }
    });
  };

  try {
    const sharedScene = await glb.load('/scene/shared.glb');
    forceOpaqueMaterials(sharedScene);
    contentRoot.add(sharedScene);
    indexHeroes([sharedScene]);
  } catch (e) {
    console.warn('[scene] shared.glb load failed', e);
  }

  const heroes = new HeroLoader(glb);
  try {
    const heroObjects = await heroes.loadFromManifest('/heroes/manifest.json');
    for (const h of heroObjects) {
      forceOpaqueMaterials(h);
      forceDoubleSidedMaterials(h);
      contentRoot.add(h);
    }
    indexHeroes(heroObjects);
  } catch (e) {
    console.warn('[heroes] manifest load failed', e);
  }

  // ── Cross-hero groups ───────────────────────────────────────────────
  // Any placement with `userData.group_id: "<name>"` joins a shared parent
  // empty so the dev-panel gizmo can drag the whole group as one. Members
  // keep their identity (per-instance hero_id, individual gizmo edit still
  // works) — the group is just an additional handle.
  //
  // Save flow uses getWorldPosition(), so moving the group bakes the
  // translation into each member's stored position on the next save. The
  // group itself returns to identity on reload (re-built from manifest).
  //
  // Hierarchy preference: when a multi-placement hero has an `#all` wrapper
  // AND all its placements agree on group_id, we attach the `#all` group to
  // the cross-group (not its individual children). That preserves the
  // per-hero "(set)" handle — picking `hero_cassette_raw_a (set)` from the
  // dropdown still drags all of that hero's cassettes as one. Yanking the
  // children out instead would orphan `#all` and the (set) handle would
  // drag nothing.
  {
    const byGroupId = new Map<string, Object3D[]>();
    const consumed = new Set<Object3D>();

    // Pass 1: #all wrappers whose children unanimously agree on a group_id.
    for (const [id, obj] of heroLookup) {
      if (!id.endsWith('#all')) continue;
      // Item 4 — keep the table cassettes OUT of the shop_set set-drag group so
      // each stays an independent, distinctly-clickable tape (pairs with the
      // Blender lane fanning + de-clipping them). They keep their own per-hero
      // `#all` (set) handle; they just don't ride the whole-shop group.
      if (id.startsWith('hero_cassette')) continue;
      const children = obj.children;
      if (children.length === 0) continue;
      const gids = new Set<string>();
      for (const c of children) {
        const gid = c.userData?.group_id;
        if (typeof gid === 'string' && gid.length > 0) gids.add(gid);
        else { gids.clear(); break; }  // any disagreement disqualifies the wrapper
      }
      if (gids.size !== 1) continue;
      const gid = gids.values().next().value as string;
      if (!byGroupId.has(gid)) byGroupId.set(gid, []);
      byGroupId.get(gid)!.push(obj);
      // Mark children as consumed so pass 2 doesn't double-attach them.
      for (const c of children) consumed.add(c);
    }

    // Pass 2: individual instances not already covered by an #all wrapper.
    for (const obj of heroLookup.values()) {
      if (consumed.has(obj)) continue;
      const hid = getHeroId(obj);
      if (hid && hid.startsWith('hero_cassette')) continue; // item 4 — cassettes stay independent
      const gid = obj.userData?.group_id;
      if (typeof gid !== 'string' || gid.length === 0) continue;
      if (!byGroupId.has(gid)) byGroupId.set(gid, []);
      byGroupId.get(gid)!.push(obj);
    }

    for (const [groupId, members] of byGroupId) {
      const groupObj = new Group();
      groupObj.userData.hero_id = `group:${groupId}`;
      groupObj.userData.group_id_self = groupId;
      contentRoot.add(groupObj);
      // .attach() reparents while preserving world transforms — members
      // visually stay put, but moving groupObj.position now moves all of them.
      for (const member of members) {
        groupObj.attach(member);
      }
      heroLookup.set(`group:${groupId}`, groupObj);
    }
    if (byGroupId.size > 0) {
      console.log(`[heroes] built ${byGroupId.size} cross-hero group(s):`,
        Array.from(byGroupId.keys()));
    }
  }

  // ── Atmosphere ───────────────────────────────────────────────────────
  // Shaft positions/radii/tunables live in public/atmosphere/morning-shaft.json
  // so the user's last-saved values survive a refresh.
  let morningShaftConfig: MorningShaftConfig | undefined;
  try {
    const res = await fetch('/atmosphere/morning-shaft.json', { cache: 'no-store' });
    if (res.ok) {
      const data = (await res.json()) as {
        shafts?: Array<{ origin: number[]; aim: number[]; radius: number }>;
        shaftIntensity?: number;
        fogDensity?: number;
        dustOpacity?: number;
        dustSize?: number;
        dustCount?: number;
      };
      morningShaftConfig = {};
      if (data.shafts && data.shafts.length) {
        morningShaftConfig.shafts = data.shafts.map((s): ShaftDef => ({
          origin: new Vector3(s.origin[0], s.origin[1], s.origin[2]),
          aim: new Vector3(s.aim[0], s.aim[1], s.aim[2]),
          radius: s.radius,
        }));
      }
      if (typeof data.shaftIntensity === 'number') morningShaftConfig.shaftIntensity = data.shaftIntensity;
      if (typeof data.fogDensity === 'number') morningShaftConfig.fogDensity = data.fogDensity;
      if (typeof data.dustOpacity === 'number') morningShaftConfig.dustOpacity = data.dustOpacity;
      if (typeof data.dustSize === 'number') morningShaftConfig.dustSize = data.dustSize;
      if (typeof data.dustCount === 'number') morningShaftConfig.dustCount = data.dustCount;
    }
  } catch (e) {
    console.warn('[atmosphere] config load failed, using defaults', e);
  }

  // Time-of-day clock + SunRig — unified lighting source. Created BEFORE
  // atmospheres so MorningShaft.init() can read the sun direction. The demo
  // opens at 6am (t=0.25) and the daylight window runs 6am→6pm (dayEnd 0.75 =
  // 18/24 = sunset — ends a touch darker at dusk, John 2026-06-29), wrapping back
  // to dawn — so the day reads as a working shop day, not
  // a midnight-inclusive cycle. Auto-cycle is OFF until the generator (demo:
  // power.onChange sets running=true; dev drives it from the timeline).
  const clock = new TimeOfDayClock({ initialT: 0.25, dayStart: 0.25, dayEnd: 0.75, dayLengthSeconds: 600, running: false });
  const sunRig = new SunRig(scene, clock);

  // CloudSky is the 3D sky dome. Lives alongside the atmosphere selector
  // (not part of it) so MorningShaft / NoAtmosphere swaps don't affect
  // the sky. Always on.
  const cloudSky = new CloudSky(sunRig);
  cloudSky.add(scene);

  const atmosphereCtx = { scene, camera, sunRig };
  const atmospheres: Record<string, Atmosphere> = {
    'morning-shaft': new MorningShaft(morningShaftConfig),
    'none': new NoAtmosphere(),
  };
  const initialAtmosphereName = 'morning-shaft';
  let activeAtmosphere: Atmosphere = atmospheres[initialAtmosphereName];
  activeAtmosphere.init(atmosphereCtx);

  const setAtmosphere = (name: string): void => {
    const next = atmospheres[name];
    if (!next || next === activeAtmosphere) return;
    activeAtmosphere.dispose(atmosphereCtx);
    activeAtmosphere = next;
    activeAtmosphere.init(atmosphereCtx);
    // Re-attach the cull plane to any new atmosphere materials. Idempotent.
    // Skip shaft handles — they're editor helpers, not world geometry.
    wallCull.attach((mesh) => !!mesh.userData.atmosphereHandle);
  };
  const getActiveAtmosphere = (): Atmosphere => activeAtmosphere;

  // ── Wall cull ────────────────────────────────────────────────────────
  // Construct AFTER atmosphere init so cones/dust participate in the clip.
  // Atmosphere handles (shaft origin/aim spheres) are excluded — they're
  // helpers, not part of the world geometry.
  const wallCull = createWallCull({
    scene,
    camera,
    interiorAABB,
    cullSettings,
    getView,
    getActiveCamera,
    cameras,
  });
  wallCull.attach((mesh) => !!mesh.userData.atmosphereHandle);
  console.log(`[cull] attached clip plane to ${wallCull.size()} materials`);

  // ── State + transition ──────────────────────────────────────────────
  const stateController = new StateController();
  const transitions: Record<string, Transition> = {
    'opacity-crossfade': new OpacityCrossfade(),
    'instant-swap': new InstantSwap(),
  };
  const initialName = 'opacity-crossfade';
  let active: Transition = transitions[initialName];
  active.init(scene);

  const setTransition = (name: string): void => {
    const next = transitions[name];
    if (!next || next === active) return;
    active.dispose();
    active = next;
    active.init(scene);
  };

  // ── Pointer + interaction engine ────────────────────────────────────
  const pointer = new PointerInteraction(renderer.domElement, camera, contentRoot);
  pointer.attach();

  // Per-era beds + music live in public/states.json; the SoundscapeEngine
  // (below) reads them as placements and plays them level-triggered with an
  // era crossfade — so buildStateRules is no longer in the rule composition.
  // What's left here is the static interaction rules (cassettes, boombox).
  const stateConfig = await loadStateConfig();
  // Stage 2: one unified file — `placements` (beds + music) + `cues` — drives
  // the whole scheduled soundscape. If soundscape.json is absent (a pre-Stage-2
  // checkout, or a hand delete) we migrate from the legacy states.json +
  // audio-cues.json so nothing authored is lost.
  const soundscapeDoc = await loadSoundscape('/soundscape.json', async () => ({
    placements: placementsFromStateConfig(stateConfig),
    cues: await loadAudioCues(),
  }));
  const audioCues = soundscapeDoc.cues;
  const composedRules = [...RULES];

  // Registry is DATA now (A13 + the registry→JSON contract change): fetched
  // from public/audio/manifest.json, license-tagged per asset. Synth entries
  // carry a patch and need no load(); samples decode as before.
  const AUDIO_ASSETS = await loadAudioManifest();
  const audio = new AudioManager();
  audio.registerAssets(AUDIO_ASSETS);
  // E2 — seed the entry low-pass to the boot view (always exterior here →
  // muffled-through-the-wall) so it's consistent with where setView leaves it.
  audio.setEntryOpenness(0);
  // A4 — don't block the first frame on decoding all ~25 samples (~36MB); the
  // scene boots muted, so nothing sounds until the first pointerdown anyway.
  // Await ONLY the opening era's bed samples (what plays the instant the visitor
  // unmutes); stream everything else (music, the other era, cue one-shots) in
  // the background. The bed reconciler skips a still-streaming bed silently and
  // retries per frame; the music slot retries on buffer-arrival (musicLoadPending)
  // — so streamed assets slot in as they decode, with no "[audio] not loaded" storm.
  const openingEra = stateController.current;
  const eagerAssetIds = new Set<string>();
  for (const p of soundscapeDoc.placements) {
    if (p.kind !== 'bed' || !(p.scope === openingEra || p.scope === 'both')) continue;
    // Both the base asset AND the oneOf pool can play (oneOf overrides asset),
    // so eager-load the whole pool — otherwise the bed picks a still-streaming
    // variant on the first unmute frame.
    if (p.asset) eagerAssetIds.add(p.asset);
    for (const id of p.vary?.oneOf ?? []) eagerAssetIds.add(id);
  }
  const samplesToLoad = AUDIO_ASSETS.filter((a) => a.kind !== 'synth' && a.url);
  const eagerStart = performance.now();
  await Promise.all(
    samplesToLoad.filter((a) => eagerAssetIds.has(a.id)).map((a) => audio.load(a.id, a.url!)),
  );
  // Stream the rest — load() dedupes + swallows its own errors, so fire-and-forget.
  let streamed = 0;
  for (const a of samplesToLoad) {
    if (!eagerAssetIds.has(a.id)) { void audio.load(a.id, a.url!); streamed++; }
  }
  if (devMode) {
    console.log(
      `[boot] A4: awaited ${eagerAssetIds.size} opening-era bed sample(s) in ` +
      `${(performance.now() - eagerStart).toFixed(0)}ms; streaming ${streamed} more`,
    );
  }

  // The generator gate (N8): session-scoped one-way latch. Stage one of the
  // awakening is arm() below (first pointerdown resumes the AudioContext —
  // the world's sounds); stage two is the generator click (power.on — the
  // shop's voice). Refresh resets by design.
  const power = createPowerLatch();
  // N8 dev-bypass (campaign 2026-06-12): ?dev=1 boots POWERED so desk/scripting
  // sessions never re-pull the cord. The demo view keeps the dark-shop opening.
  if (devMode) power.on();
  // Demo: powering on the shop (the generator pull-start) WAKES the scene —
  // unmute the master + start the day cycle. Until then the world is silent
  // (boot is muted) and the day clock is frozen; the generator click is the
  // visitor's first sound and the moment the cycle begins. Dev boots powered
  // (above) and drives mute/clock from the timeline, so this is demo-only.
  if (!devMode) {
    power.onChange(() => {
      if (power.powered) {
        void audio.resume();
        audio.setMuted(false);
        clock.running = true; // the generator pull starts the day
      } else {
        clock.running = false; // item 3 — closing up: the day stops at dusk
      }
    });
  }

  // Item 2 — the shop's central light follows the generator. The `bulb` mesh
  // (pendant fixture in shared.glb) glows + a PointLight lights the room when
  // powered; both go dark when the generator is switched off (item 3). Wired
  // unconditionally: dev boots powered → lit; demo opens dark until the pull-start.
  let foundBulb: Mesh | null = null;
  scene.traverse((o) => { if (!foundBulb && (o as Mesh).isMesh && o.name === 'bulb') foundBulb = o as Mesh; });
  const shopBulb = foundBulb as Mesh | null; // re-widen past TS's closure-mutation narrowing
  const shopLight = new PointLight(0xffd9a0, 0, 7, 1.4); // warm tungsten; intensity driven by power
  if (shopBulb) shopBulb.getWorldPosition(shopLight.position);
  else shopLight.position.set(-0.71, 2.43, -0.71); // known bulb world pos (fallback if unnamed)
  scene.add(shopLight);
  const SHOP_LIGHT_INTENSITY = 2.4; // ⚑ John tunes the lit brightness
  const setShopLight = (on: boolean): void => {
    shopLight.intensity = on ? SHOP_LIGHT_INTENSITY : 0;
    if (shopBulb) {
      const mats = Array.isArray(shopBulb.material) ? shopBulb.material : [shopBulb.material];
      for (const m of mats) {
        const std = m as MeshStandardMaterial;
        if (std.emissive) { std.emissive.setHex(on ? 0xffcaa0 : 0x000000); std.emissiveIntensity = on ? 1.6 : 0; }
      }
    }
  };
  setShopLight(power.powered);
  power.onChange(() => setShopLight(power.powered));

  // #8 — darker shop BEFORE the generator (Turki: "the atmosphere should appear a
  // bit darker before turning the generator on"). While unpowered (demo only), dim
  // the GLOBAL sun + ambient that sunRig writes each frame, so the generator-pull
  // reads as a world-brightening reveal (on top of the ceiling light coming on).
  // The factor ramps so it's a felt swell, not a snap. Dev keeps full lighting
  // (authoring). ⚑ John tunes PRE_GEN_DIM (how dark) by eye.
  const PRE_GEN_DIM = 0.65; // unpowered light multiplier — "a bit darker"
  let preGenDim = (!devMode && !power.powered) ? PRE_GEN_DIM : 1;

  // Item 3 — the generator re-click powers the shop DOWN in the day's closing
  // window (a deliberate end-of-day ritual). First click (unpowered) pulls it on;
  // a later click, once powered AND within the last window before 6pm (dayEnd
  // 0.75), powers down → bulb dark (item 2) + requiresPower beds fade (soundscape
  // reads the latch live). Outside the window a re-click is a no-op. The pull SFX
  // lives in the generator rule; this owns the power STATE (clock + latch in scope).
  const CLOSE_WINDOW_T = 0.70; // ⚑ ≈ last hour before 6pm; John tunes
  pointer.on('click', (info) => {
    if (info.heroId !== 'hero_generator') return;
    if (!power.powered) power.on();
    else if (clock.t >= CLOSE_WINDOW_T) power.off();
  });

  // Forward-declared so the cassette/boombox rules can drive the music slot the
  // SoundscapeEngine owns (constructed just below; only reached on a click,
  // long after assignment).
  let soundscape!: SoundscapeEngine;
  const engine = new InteractionEngine({
    audio,
    state: stateController,
    setCamera,
    pointer,
    rules: composedRules,
    getObjectByHeroId: resolveHeroObject, // item 8 — era-visible instance for multi-placement heroes
    setMusicOverride: (assetId, at, volume) => soundscape.setMusicOverride(assetId, at, volume),
    clearMusicOverride: () => soundscape.clearMusicOverride(),
    power,
  });
  engine.arm();

  // Scheduled soundscape — the single per-frame reconciler for beds (level-
  // triggered, era-crossfaded), the diegetic music slot (era default + cassette
  // override), and one-shot cues (edge-fired from the live audioCues array).
  // Beds + music + one-shots all come from the unified soundscape doc (loaded
  // above), mutated live by the timeline lanes. Ticked in the render loop below.
  const scenePlacements = soundscapeDoc.placements;
  // Sewing-machine hum: OFF until the visitor clicks the machine, then a toggle
  // (John, 2026-06-29 — directors Bentley + Turki both heard it run non-stop with
  // no off-switch). Gate every bed anchored to hero_sewing on this latch; the
  // hero_sewing click handler (below) flips it. Demo-only intent, but harmless in
  // dev (sewing just starts silent until clicked).
  let sewingEngaged = devMode; // dev authoring keeps the bed audible by default
  const sewingBedIds = new Set(
    scenePlacements.filter((p) => p.kind === 'bed' && p.at?.heroId === 'hero_sewing').map((p) => p.id),
  );
  const morphProgress = (): number =>
    stateController.target === 'present' ? stateController.progress : 1 - stateController.progress;
  soundscape = new SoundscapeEngine({
    audio,
    getPlacements: () => scenePlacements,
    getCues: () => audioCues,
    getObjectByHeroId: resolveHeroObject, // item 8 — era-visible instance for multi-placement heroes
    currentEra: () => stateController.current,
    morph: morphProgress,
    fireCue: (cue) => {
      engine.run(cue.actions);
      devBus.emit('cue:fire', { id: cue.id });
    },
    // N1 clips map day-t ↔ real seconds through the live day length.
    dayLengthSeconds: () => clock.dayLengthSeconds,
    // E3 — beat 6: the tape ends and the era exhales back to the present.
    // setTarget runs a normal timed transition (the ⚙ morph duration) and
    // no-ops if the room is already present.
    onTapeEnded: () => stateController.setTarget('present'),
    // N8: electrical placements wait for the generator latch.
    powered: () => power.powered,
    // Sewing hum is OFF until the machine is clicked (toggle) — see sewingEngaged.
    bedEnabled: (id) => (sewingBedIds.has(id) ? sewingEngaged : true),
    // Demo: the era's default music never auto-plays — music is summoned only by
    // a cassette (silence-until-cassette). Dev keeps era defaults for authoring.
    autoEraMusic: () => devMode,
  });

  // Sewing machine → toggle its hum on/off (John, 2026-06-29). The bed starts
  // silent; clicking hero_sewing engages it, clicking again stops it — so the
  // hum is "prompted by using the machine" rather than running non-stop. The
  // bedEnabled gate (above) reads sewingEngaged live, so the bed fades in/out.
  pointer.on('click', (info) => {
    if (info.heroId !== 'hero_sewing') return;
    sewingEngaged = !sewingEngaged;
  });

  // ── Hover + selection highlight ─────────────────────────────────────
  // Always-on visual feedback: emissive boost on hover, stronger boost +
  // inverted-hull outline on click-selected hero. Independent of the rules
  // engine — even heroes with no click rule (placeholder spheres, cloth
  // pieces) still light up on hover.
  // DM7 — "selected = editable": a 3D click on a hero should attach the gizmo,
  // not just highlight it. onPick routes the click through setEditTarget (defined
  // below, so late-bound here); in demo, select:false short-circuits the click
  // before onPick, so this is authoring-only.
  let onHeroPick: ((heroId: string | null) => void) | null = null;
  const hoverHighlight = new HoverHighlight({
    pointer,
    select: devMode,
    onPick: (id) => { if (onHeroPick) onHeroPick(id); else devBus.emit('hero:select', { id }); },
  });

  // ── Cloth sim on the table ──────────────────────────────────────────
  // Two cloth patches replace the static fabric meshes that used to ship
  // inside hero_table.glb (FrontCloth-table_hero, Sidecloth-table_hero
  // were removed in Blender + re-exported). Pin lines are derived from
  // the live table's world bbox so any future dev-panel reposition of
  // the table carries the cloths along without code changes.
  //
  // Front cloth: pinned along the table's front edge (the +Z face that
  //              faces the doorway camera) — spans the full table width.
  // Side cloth:  pinned along the table's right edge (+X face) — spans
  //              the full table depth.
  //
  // 4mm forward / sideways inset on the pin line so the cloth doesn't
  // z-fight the table top at the seam.
  const cloths: ClothPatch[] = [];
  const tableRoot = heroLookup.get('hero_table');
  if (tableRoot) {
    const tableBox = new Box3().setFromObject(tableRoot);
    const topY = tableBox.max.y;
    const minX = tableBox.min.x;
    const maxX = tableBox.max.x;
    const minZ = tableBox.min.z;
    const maxZ = tableBox.max.z;
    // floor a touch below the table-bottom so the cloth can settle to a
    // natural drape (legs go nearly to ground).
    const floorY = tableBox.min.y - 0.02;

    // Pin lines are pulled in 1cm from the geometric edge so the cloth
    // overlap reads as the cloth draping from the top instead of clipping
    // through the table top corner.
    const inset = 0.01;
    const frontPinZ = maxZ - inset;
    const sidePinX = maxX - inset;
    // Vertical drop: from table-top down to a hair above floor (let the
    // last row dangle into the floor slightly so it reads as resting).
    const hangHeight = topY - floorY - 0.02;

    // C3: a single axis-box collider for the table volume so wind/grabs can't
    // push a skirt through the tabletop; the floor plane stays via floorY.
    const tableCollider = {
      type: 'box' as const,
      min: [tableBox.min.x, tableBox.min.y, tableBox.min.z] as [number, number, number],
      max: [tableBox.max.x, tableBox.max.y, tableBox.max.z] as [number, number, number],
    };

    const frontCloth = new ClothPatch({
      id: 'tablecloth_front',
      pinStart: new Vector3(minX + inset, topY, frontPinZ),
      pinEnd:   new Vector3(maxX - inset, topY, frontPinZ),
      height: hangHeight,
      cols: 14,
      rows: 10,
      windStrength: 1.0,
      gravity: 3.0,
      floorY,
      colliders: [tableCollider],
    });
    frontCloth.mesh.userData.hero_id = 'cloth_table_front';
    frontCloth.mesh.userData.interactive = true;
    contentRoot.add(frontCloth.mesh);
    cloths.push(frontCloth);

    const sideCloth = new ClothPatch({
      id: 'tablecloth_side',
      pinStart: new Vector3(sidePinX, topY, minZ + inset),
      pinEnd:   new Vector3(sidePinX, topY, maxZ - inset),
      height: hangHeight,
      cols: 12,
      rows: 10,
      windStrength: 0.8,
      gravity: 3.0,
      floorY,
      colliders: [tableCollider],
    });
    sideCloth.mesh.userData.hero_id = 'cloth_table_side';
    sideCloth.mesh.userData.interactive = true;
    contentRoot.add(sideCloth.mesh);
    cloths.push(sideCloth);
  } else {
    console.warn('[cloth] hero_table not found — skipping cloth instantiation');
  }

  // Item 7 — carry the cloths WITH the table. Their pins are derived from the
  // table's world bbox at load, so a live reposition (dev gizmo) would otherwise
  // strand them. The render loop polls the table's world position and rigid-
  // translates the cloths by any delta, so moving the table moves the cloth.
  const clothTablePrevPos = new Vector3();
  { const t0 = heroLookup.get('hero_table'); if (t0) t0.getWorldPosition(clothTablePrevPos); }
  const clothFollowScratch = new Vector3();

  // Mute OrbitControls during cloth grab by reaching into whatever camera
  // is active — mirrors the gizmo-drag suppression pattern below.
  const cameraControlsToggle = {
    get enabled(): boolean {
      const c = (activeCamera as unknown as { controls?: { enabled: boolean } }).controls;
      return c?.enabled ?? true;
    },
    set enabled(v: boolean) {
      const c = (activeCamera as unknown as { controls?: { enabled: boolean } }).controls;
      if (c) c.enabled = v;
    },
  };
  const clothGrab = new ClothGrabController({
    camera,
    domElement: renderer.domElement,
    controlsToggle: cameraControlsToggle,
  });
  for (const cloth of cloths) clothGrab.register(cloth);
  clothGrab.attach();

  // The chair is a SPARSE monoblock: from the entering rail it's low, far, and
  // partly below the frame, and only ~17% of its silhouette is solid geometry —
  // so clicking it to sit is a constant near-miss (John: "unable to click the
  // chair at the default first position"). Add an invisible click-PROXY sized to
  // its lower mass (seat + legs) so the whole chair area is a reliable sit target.
  // Capped at y≈0.30 — below the table cassettes (~0.40) — so it can't steal their
  // clicks. Transparent + no depth write (renders nothing, no occlusion) but
  // visible:true so the WYSIWYG render-chain check accepts it; tagged hero_chair so
  // a hit resolves to the chair, no_highlight so the glow never lights the box.
  {
    const chairRoot = heroLookup.get('hero_chair');
    if (chairRoot) {
      const wbox = new Box3().setFromObject(chairRoot);
      const yTop = Math.min(wbox.max.y, 0.30);
      const sx = Math.max(0.01, wbox.max.x - wbox.min.x);
      const sy = Math.max(0.01, yTop - wbox.min.y);
      const sz = Math.max(0.01, wbox.max.z - wbox.min.z);
      const proxy = new Mesh(
        new BoxGeometry(sx, sy, sz),
        new MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false, colorWrite: false }),
      );
      proxy.position.set((wbox.min.x + wbox.max.x) / 2, (wbox.min.y + yTop) / 2, (wbox.min.z + wbox.max.z) / 2);
      proxy.name = 'hero_chair_clickproxy';
      proxy.userData.no_highlight = true;
      tagInteractive(proxy, 'hero_chair'); // a hit walks up to itself → heroId hero_chair
      contentRoot.add(proxy);
    }
  }

  // PF5: now that every interactive object is in contentRoot (heroes loaded
  // above, cloth just added), build the pointer's restricted cast set so
  // pointermove hover/click skips the photoscan and other static geometry.
  pointer.refreshInteractiveRoots();

  // ── Dev viz layer (Phase 2 gizmos) ───────────────────────────────────
  // Single root Group with per-category subgroups. Master + per-category
  // visibility toggles persist in localStorage. Hotkey: G toggles the
  // master. The toggle UI is folded into the inspector (Phase B).
  //
  // SINGLE DEV GATE: every dev surface — 3D gizmos, timeline, event log,
  // hotkey hint, inspector, lil-gui — exists only under `?dev`. Demo /
  // viewer view is therefore truly clean. Director mode (D key) hides
  // the created surfaces within dev without destroying them. Hoisted to
  // `let … = null` so the render tick + the later dev-panel block can
  // reference them; null-guarded at the callsites.
  let dvScene: DebugVizScene | null = null;
  let audioGizmos: AudioGizmos | null = null;
  let clothGizmos: ClothGizmos | null = null;
  let sceneGizmos: SceneGizmos | null = null;
  // The timeline's undo (U1), set when the dev timeline is created below. The
  // gizmo undo's ⌘Z arbiter reads it (late-bound) to pick the most-recently-
  // edited surface; null in non-dev → gizmo undo behaves normally.
  let timelineUndo: { lastEditAt: () => number; undo: () => boolean; redo: () => boolean; lastUndoneAt: () => number } | null = null;
  // DM6 — the one save model coordinator. Created before the timeline so the
  // timeline can register the soundscape as a delegate; the owned aspects
  // (manifest, atmosphere) register once `saves` exists below. Drives save-all,
  // ⌘S, save-on-commit, and the global unsaved readout.
  const saveHub = createSaveHub();
  if (devMode) {
    dvScene = new DebugVizScene();
    dvScene.add(scene);
    audioGizmos = new AudioGizmos(dvScene.groups.audio, {
      getPlacements: () => scenePlacements,
      getObjectByHeroId: resolveHeroObject, // item 8 — era-visible instance for multi-placement heroes
      activeGain: (pid) => soundscape.activeGain(pid),
    });
    clothGizmos = new ClothGizmos(dvScene.groups.cloth);
    for (const cloth of cloths) clothGizmos.register(cloth);
    sceneGizmos = new SceneGizmos({
      lightingGroup: dvScene.groups.lighting,
      cullingGroup: dvScene.groups.culling,
      camerasGroup: dvScene.groups.cameras,
      interiorAABB,
      sunRig,
      wallCull,
    });
    for (const [, obj] of heroLookup) {
      if (!obj.userData?.hero_id) continue;
      // interactive defaults to true if the manifest didn't set it explicitly.
      const interactive = obj.userData.interactive !== false;
      sceneGizmos.registerHero(obj, interactive);
    }

    // ── Unified selection ───────────────────────────────────────────────
    // One "active hero" lights up everywhere. HoverHighlight emits
    // hero:select on a 3D click; setEditTarget emits it on an inspector row
    // click. Fan that single signal out to the AABB highlight + the 3D
    // outline. hoverHighlight.selectRoot is emit-free, so this can't loop.
    devBus.on('hero:select', ({ id }) => {
      sceneGizmos?.setSelected(id);
      hoverHighlight.selectRoot(id ? (heroLookup.get(id) ?? null) : null);
    });

    // ── Cross-surface audio focus ───────────────────────────────────────
    // The hover analogue of the unified selection. A hover on any audio
    // surface (timeline SOUND chip / BED bar / CUE marker, inspector palette
    // chip / channel fader / playing-now row) broadcasts an audio:focus; when
    // it carries a hero we light that hero's mesh here in 3D. focusRoot is
    // emit-free, so this never loops back. (The 3D source spheres listen to
    // audio:focus themselves.) Conversely a raw 3D hero hover is re-broadcast
    // as audio:focus so those same surfaces illuminate in return — one signal,
    // every surface, both directions.
    devBus.on('audio:focus', ({ ref }) => {
      hoverHighlight.focusRoot(ref?.heroId ? (heroLookup.get(ref.heroId) ?? null) : null);
    });
    devBus.on('hero:hover', ({ id }) => {
      devBus.emit('audio:focus', { ref: id ? { heroId: id } : null });
    });

    // Timeline strip — bottom of viewport, two lanes (time of day + state
    // morph), transport + hotkeys. Polls clock/state for the playhead each
    // frame; mutates them on scrub.
    timelineUndo = createTimeline({
      clock,
      state: stateController,
      audio,
      transitions,
      setTransition,
      initialTransition: initialName,
      cues: audioCues,
      placements: scenePlacements,
      // Both lanes (CUES one-shots, AMBIENCE beds) persist the whole unified
      // doc — the live arrays are the source, version rides from the load.
      // Returns the SaveResult so the timeline shows saved ✓ / disk-changed / ✕;
      // `o` carries { force } for the overwrite-past-conflict path.
      saveSoundscape: (o) => saves.saveSoundscape({ version: soundscapeDoc.version, placements: scenePlacements, cues: audioCues, dropped: soundscapeDoc.dropped }, o),
      // DM6 — register soundscape as a hub delegate; ⌘S + save-all flush everything.
      saveHub,
      audioAssets: AUDIO_ASSETS,
      heroIds: [...heroLookup.keys()].filter((k) => !k.includes('#') && !k.startsWith('group:')).sort(),
      // Entries on disk that failed to parse at load (S2) — surfaced as a warning
      // chip so the author knows the file carries quarantined (unfunctional) data.
      droppedCount: (soundscapeDoc.dropped?.placements.length ?? 0) + (soundscapeDoc.dropped?.cues.length ?? 0),
      // N3 audition-in-context: cues run their RAW actions through the real
      // engine path (deterministic — no vary roll); clips play their trimmed
      // slice through the real chain at their anchor, toggle to stop.
      runActions: (actions) => engine.run(actions),
      // N4: the slot's live truth for the persistent MUSIC strip.
      musicStatus: () => soundscape.musicStatus(stateController.current),
      // N5: the "why aren't you sounding?" answers.
      bedState: (id) => soundscape.bedState(id),
      cueFired: (id) => soundscape.cueFiredThisPass(id),
      // N9: the patch editor saves tweaked/forked synth patches to the
      // registry file and registers forks live so they play immediately.
      saveAudioManifest: () => saves.saveAudioManifest(AUDIO_ASSETS),
      registerAsset: (a) => audio.registerAssets([a]),
      auditionClip: (() => {
        let h: string | null = null;
        return (p: import('./audio/soundscape').SoundPlacement): void => {
          if (h && audio.isHandlePlaying(h)) { audio.stopHandle(h, 0.05); h = null; return; }
          if (!p.asset) return;
          const obj = p.at?.heroId ? heroLookup.get(p.at.heroId) : undefined;
          h = audio.play(p.asset, {
            channel: p.channel ?? 'narration', volume: p.gain ?? 1,
            at: obj ? { object: obj } : (p.at?.position ? { position: p.at.position } : undefined),
            spatial: p.spatial ?? (p.at ? 'point' : 'global'),
            refDistance: p.refDistance, maxDistance: p.maxDistance,
            offset: p.in ?? 0,
          });
          // Stop at the slice's end (a UI act, not engine scheduling — timer ok).
          const sliceSec = Math.min(p.dur ?? 8, (p.out ?? Infinity) - (p.in ?? 0));
          if (h && Number.isFinite(sliceSec) && sliceSec > 0) {
            const hh = h;
            setTimeout(() => { if (audio.isHandlePlaying(hh)) audio.stopHandle(hh, 0.05); }, sliceSec * 1000);
          }
        };
      })(),
    });
    // Event log — narrator strip above the timeline. Subscribes to every
    // devBus event and surfaces them as fading one-liners.
    createEventLog();
    // Hotkey cheatsheet — '?' opens a modal listing every shortcut.
    createHotkeyHint();
  }

  // ── Gizmo ───────────────────────────────────────────────────────────
  // ONE TransformControls. Mode toggle lives in the dev panel "edit" folder:
  // translate by default, switch to rotate when needed (E key as shortcut).
  // Showing both gizmos at once meant translate arrows and rotate rings
  // shared the same gizmo origin and converged at small camera distances —
  // grabbing one frequently caught the other, producing rotations during
  // what felt like a translate drag. Single gizmo = no overlap, no surprise.
  //
  // `editable` targets respect the mode dropdown. `translate-only` targets
  // (shaft handles) force translate and reject mode-switch attempts —
  // rotating a position-only handle is meaningless.
  const tc = new TransformControls(camera, renderer.domElement);
  tc.setMode('translate');
  const helper = tc.getHelper();
  helper.visible = false;
  tc.enabled = false;
  scene.add(helper);
  const onGizmoDrag = (event: unknown): void => {
    const dragging = (event as { value: boolean }).value;
    const fm = activeCamera as unknown as { controls?: { enabled: boolean } };
    if (fm.controls) fm.controls.enabled = !dragging;
  };
  tc.addEventListener('dragging-changed', onGizmoDrag);

  type GizmoTargetMode = 'editable' | 'translate-only';
  let currentTargetMode: GizmoTargetMode = 'editable';
  // Visible to the dev panel + the W/E/R shortcuts. Reads as the displayed
  // gizmo mode (translate / rotate / scale). Only meaningful for `editable` targets.
  let gizmoMode: 'translate' | 'rotate' | 'scale' = 'translate';

  const attachGizmo = (obj: Object3D | null, mode: GizmoTargetMode = 'editable'): void => {
    if (!obj) {
      tc.detach();
      helper.visible = false;
      tc.enabled = false;
      return;
    }
    tc.attach(obj);
    currentTargetMode = mode;
    // Locked targets always translate. Editable targets honor whatever the
    // user last set via the dropdown — but a fresh pick resets to translate
    // so the safer action is always one click away.
    if (mode === 'translate-only') {
      tc.setMode('translate');
    } else {
      gizmoMode = 'translate';
      tc.setMode('translate');
    }
    helper.visible = true;
    tc.enabled = true;
  };
  const getGizmoTarget = (): Object3D | null => (tc.object as Object3D | null) ?? null;
  const getGizmoMode = (): 'translate' | 'rotate' | 'scale' => gizmoMode;
  const setGizmoMode = (m: 'translate' | 'rotate' | 'scale'): void => {
    // Translate-only targets ignore rotate/scale requests — keeps the toggle
    // sane when a shaft handle is selected.
    if (currentTargetMode === 'translate-only' && m !== 'translate') return;
    gizmoMode = m;
    if (tc.object) tc.setMode(m);
  };

  // W = translate, E = rotate. Identical to the dropdown, just faster.
  window.addEventListener('keydown', (e) => {
    if (!tc.object) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (isTextEntryTarget(e)) return; // U2: also bail on focused <select>/contentEditable
    if (e.key === 'w' || e.key === 'W') { e.preventDefault(); setGizmoMode('translate'); }
    else if (e.key === 'e' || e.key === 'E') { e.preventDefault(); setGizmoMode('rotate'); }
    else if (e.key === 'r' || e.key === 'R') { e.preventDefault(); setGizmoMode('scale'); }
  });
  // Document these in the cheatsheet, co-located with the handler.
  hotkeyRegistry.register({ keys: 'W', description: 'translate gizmo', section: 'editing', order: 0 });
  hotkeyRegistry.register({ keys: 'E', description: 'rotate gizmo', section: 'editing', order: 1 });
  hotkeyRegistry.register({ keys: 'R', description: 'scale gizmo', section: 'editing', order: 2 });

  // Translate-purity watchdog. Empirically: while the gizmo is in 'translate'
  // mode, dragging an arrow sometimes also mutates the target's quaternion
  // / scale — visible as the hero spinning around the axis you're sliding
  // along. The translate handler in three.js TransformControls only writes
  // to object.position, so whatever's leaking rotation must be downstream
  // (cross-hero group transform accumulating, glTF root with non-identity
  // TRS, etc.). Cheap defense: snapshot quaternion + scale at drag start,
  // restore on every objectChange while in translate. The first leak per
  // drag logs to console so we can find the root cause.
  let translateBaseline: { obj: Object3D; quaternion: Quaternion; scale: Vector3 } | null = null;
  let translateLeakLogged = false;
  // DM11 — sane scale bounds, shared by the gizmo-drag clamp (below) and the
  // numeric scale path (setHeroScale). The numeric path always clamped; dragging
  // the scale handle bypassed it, so a hero could be driven to 0 or 10000×.
  const HERO_SCALE_MIN = 0.01;
  const HERO_SCALE_MAX = 100;
  const clampScale = (v: number): number => Math.min(HERO_SCALE_MAX, Math.max(HERO_SCALE_MIN, Number.isFinite(v) ? v : 1));
  tc.addEventListener('mouseDown', () => {
    const obj = tc.object;
    if (!obj || tc.getMode() !== 'translate') {
      translateBaseline = null;
      return;
    }
    translateBaseline = {
      obj,
      quaternion: obj.quaternion.clone(),
      scale: obj.scale.clone(),
    };
    translateLeakLogged = false;
  });
  tc.addEventListener('objectChange', () => {
    // DM11 — clamp scale on the gizmo drag too. The scale gizmo writes
    // obj.scale directly; without this a drag could push it to 0 (object
    // vanishes, save writes 0) or to a runaway value. Clamp every axis live so
    // the handle simply pins at the bound (mirrors the numeric path's clamp).
    if (tc.getMode() === 'scale' && tc.object) {
      const s = tc.object.scale;
      const cx = clampScale(s.x), cy = clampScale(s.y), cz = clampScale(s.z);
      if (cx !== s.x || cy !== s.y || cz !== s.z) s.set(cx, cy, cz);
    }
    if (!translateBaseline) return;
    const { obj, quaternion, scale } = translateBaseline;
    if (!obj.quaternion.equals(quaternion)) {
      if (!translateLeakLogged) {
        console.warn('[gizmo] translate drag mutated quaternion; restoring.',
          { heroId: obj.userData?.hero_id,
            baseline: quaternion.toArray(),
            leaked: obj.quaternion.toArray() });
        translateLeakLogged = true;
      }
      obj.quaternion.copy(quaternion);
    }
    if (!obj.scale.equals(scale)) {
      obj.scale.copy(scale);
    }
  });
  tc.addEventListener('mouseUp', () => {
    translateBaseline = null;
  });

  // Undo/redo for gizmo edits. Captures pre/post matrices on every drag.
  // Cmd/Ctrl-Z to undo, Cmd/Ctrl-Shift-Z (or Cmd-Y) to redo.
  const gizmoUndo = createGizmoUndo({
    controls: [tc],
    // Late-bound to the timeline's undo (U1) so ⌘Z undoes whichever surface
    // edited last — a timeline edit after a gizmo drag rolls back the timeline,
    // not the 3D transform.
    peerUndo: {
      lastEditAt: () => timelineUndo?.lastEditAt() ?? 0,
      undo: () => timelineUndo?.undo() ?? false,
      // DM8 — also expose redo + redo-recency so ⌘⇧Z walks across both surfaces.
      lastUndoneAt: () => timelineUndo?.lastUndoneAt() ?? 0,
      redo: () => timelineUndo?.redo() ?? false,
    },
    // DM6 — the aspect a drag-end commit belongs to: a hero ('editable') saves
    // to manifest.json, a shaft handle ('translate-only') to morning-shaft.json.
    currentAspect: () => (currentTargetMode === 'translate-only' ? 'atmosphere' : 'manifest'),
    // DM6 — every gizmo commit (drag-end, retag, scale) AND every undo/redo
    // re-persists its aspect through the save-on-commit hub, so the 3D edit
    // reaches disk without a separate ⌘S and an undo re-commits the reverted
    // state. (This is why the old gizmoUndo.clear()-on-save is retired below.)
    onCommit: (aspect) => { if (aspect) saveHub.markDirty(aspect); },
  });

  // B5 — numeric per-hero scale entry. The gizmo's `scale` mode handles the
  // drag; this is the typed path from the inspector. Sets the hero's local
  // scale (uniform or per-axis, clamped sane) and records the change on the
  // gizmo undo stack — so ⌘Z reverts it and ⌘S → manifest.json persists it via
  // the existing writePlacement scale path (uniform-or-per-axis). The
  // Blender↔runtime contract is untouched (scale is already in HeroPlacement).
  // DM11 — clamp via the shared clampScale (same bounds as the gizmo-drag clamp).
  const setHeroScale = (id: string, scale: { x: number; y: number; z: number }): void => {
    const obj = heroLookup.get(id);
    if (!obj) return;
    obj.updateMatrix();
    const before = obj.matrix.clone();
    obj.scale.set(clampScale(scale.x), clampScale(scale.y), clampScale(scale.z));
    obj.updateMatrix();
    const after = obj.matrix.clone();
    if (!before.equals(after)) gizmoUndo.push(obj, before, after, 'manifest'); // DM6 — auto-persist scale
  };

  /** Park the freeform orbit pivot on the camera's forward ray so the next
   *  init() is a visual no-op — preserves the user's current view when
   *  switching modes to manipulate an object. */
  const switchToFreeformPreservingView = (focusObj: Object3D): void => {
    if (activeCamera === cameras['freeform']) return;
    const focusPos = new Vector3();
    focusObj.getWorldPosition(focusPos);
    const distance = Math.max(0.5, camera.position.distanceTo(focusPos));
    const forward = new Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    const orbitTarget = camera.position.clone().add(forward.multiplyScalar(distance));
    const options: FreeformInitOptions = { target: orbitTarget };
    activeCamera.dispose();
    activeCamera = cameras['freeform'];
    (activeCamera as FreeformMode).init(options);
  };

  let currentEditTarget = '(none)';
  const setEditTarget = (heroId: string): void => {
    currentEditTarget = heroId;
    if (heroId === '(none)') {
      attachGizmo(null);
      devBus.emit('hero:select', { id: null });
      return;
    }
    const obj = heroLookup.get(heroId);
    if (!obj) {
      // No live object for this id — a bulk handle (group:/#all), or a target
      // that no longer exists (hero removed). DM2: detach the gizmo so a prior
      // selection's handle can't linger on a thing you can no longer point at
      // (it would stay draggable + saveable over a ghost). Bulk handles get no
      // gizmo anyway, so this is correct for both; the row still highlights.
      attachGizmo(null);
      devBus.emit('hero:select', { id: heroId });
      return;
    }
    attachGizmo(obj, 'editable');
    switchToFreeformPreservingView(obj);
    devBus.emit('hero:select', { id: heroId });
  };
  const getEditTarget = (): string => currentEditTarget;
  // DM7 — now that setEditTarget exists, bind the 3D-click pick handler: a click
  // on a hero in the scene attaches the gizmo (mirrors an inspector-row click).
  // setEditTarget emits hero:select, so the AABB + outline still light up.
  onHeroPick = (heroId: string | null): void => setEditTarget(heroId ?? '(none)');

  // ── Camera handles ──────────────────────────────────────────────────
  // Two always-visible read-only markers (green = exterior, cyan = doorway)
  // showing where the saved camera anchors live. Editing is camera-snapshot
  // only: orbit the live camera, hit "set outside view here" or "set doorway
  // here" — the corresponding Vector3 updates and the marker follows on the
  // next syncToSources tick. No gizmo attaches to these markers.
  const cameraHandles = createCameraHandles(scene, exteriorPos, doorway);

  // ── Shaft handles ───────────────────────────────────────────────────
  const morningShaft = atmospheres['morning-shaft'] as MorningShaft;
  const shaftHandleIds: string[] = ['(none)'];
  for (let i = 0; i < morningShaft.getShaftCount(); i++) {
    shaftHandleIds.push(`shaft ${i} origin`, `shaft ${i} aim`);
  }
  const setShaftEditTarget = (id: string): void => {
    if (id === '(none)') {
      morningShaft.setHandlesVisible(false);
      attachGizmo(null);
      return;
    }
    const m = id.match(/^shaft (\d+) (origin|aim)$/);
    if (!m) return;
    const index = parseInt(m[1], 10);
    const role = m[2] as 'origin' | 'aim';
    const handle = morningShaft.getShaftHandle(index, role);
    if (!handle) return;
    morningShaft.setHandlesVisible(true);
    attachGizmo(handle, 'translate-only');
    switchToFreeformPreservingView(handle);
  };

  // Now that all gizmo targets exist, wire the detach helper that
  // tweenToPose / setView call before re-attaching the camera mode.
  // (Camera handle markers are passive visualizations — nothing to detach.)
  detachGizmos = () => {
    attachGizmo(null);
    morningShaft.setHandlesVisible(false);
  };

  // ── Save flows ──────────────────────────────────────────────────────
  const r3 = (n: number): number => Math.round(n * 1000) / 1000;
  const r3v = (v: Vector3): [number, number, number] => [r3(v.x), r3(v.y), r3(v.z)];
  const snapshotPose = (): { position: [number, number, number]; target: [number, number, number] } => ({
    position: r3v(camera.position),
    target: r3v(currentTarget()),
  });

  const saves = createSaveFlows({
    heroLookup,
    morningShaft,
    cameras,
    exteriorPos,
    exteriorTarget,
    doorway,
    interiorAABB,
    cullSettings,
    bookmarks,
    snapshotPose,
    // Seeds the soundscape freshness guard — the hash of the bytes loaded at
    // boot, sent as If-Match so a save refuses if the file changed on disk.
    soundscapeBaseHash: soundscapeDoc.baseHash,
    // Capture the current Moment coordinate (time + era) alongside the pose
    // when a bookmark is saved.
    snapshotMoment: () => ({ t: clock.t, morph: morphProgress() }),
  });

  // DM6 — register the OWNED aspects with the save hub now that `saves` exists.
  // These auto-persist on commit (a finished gizmo drag / retag / scale) via the
  // hub's debounce, and flush on ⌘S / save-all / their per-aspect button. The
  // client-side conflict guard inside each flow still gates the write. (Camera
  // stays deliberate — its saves ARE button presses, so it has no dirty limbo.)
  saveHub.registerOwned('manifest', () => saves.saveHeroPositions());
  saveHub.registerOwned('atmosphere', () => saves.saveShaftConfig());

  // ── UI ──────────────────────────────────────────────────────────────
  // Player-facing surfaces (always visible): logo badge, mute pill + master
  // volume, view toggle. The dev surfaces (lil-gui panel, per-hero state
  // radios, hero-id hover chip, camera anchor markers, the verbose tracks
  // bar listing every audio asset) only show under `?dev=1`.
  const toggleView = (): void => setView(getView() === 'exterior' ? 'interior' : 'exterior');

  // Top-left brand badge — present in both demo and dev views. Clicking the
  // badge opens the project brief (public/brief.html) in a new tab.
  createLogoBadge({ href: '/brief.html' });

  // Persistent audio control — docked top-left under the logo, present in ALL
  // views (demo, dev HUD, director). Relocated from the old bottom-center pill,
  // which overlapped the timeline in dev and was hidden there — leaving the dev
  // view with no quick mute (only the buried inspector AUDIO-tab checkbox). Now
  // it's always available and reads as one elegant cluster with the logo.
  createAudioControls(audio);
  createViewToggle(getView, toggleView, () => power.powered, !devMode); // N8: dark shop refuses entry; demo hides the top go-inside on first launch (the step-inside pill carries entry)

  // (Asset audition chips + per-hero ambient beds are now folded into the
  //  inspector AUDIO tab — see createInspector below.)

  // Small ⚙ button top-right in demo view → opens dev view via ?dev=1.
  // Not shown in dev view because lil-gui already lives top-right there.
  if (!devMode) createDevToggle(false);

  // P1 #9 — the generic controls hint ("drag to look · click cassettes · go
  // inside") is SUPPRESSED during the guided first-run: the story-director owns
  // guidance there, and the hint's text otherwise competes with the step-by-step
  // card. It's deferred to the reward (RETURN→SANDBOX, see onReward below), so
  // free-play camera guidance appears only once the loop completes. Dev never
  // shows it. (No-director fallback handled there too.)

  // Demo-only player surfaces: the click-feedback chip (replaces the selection
  // outline) + the simplified timeline (day cycle + era marker, revealed on
  // power). The full NLE + persistent selection stay under ?dev=1.
  let demoTimeline: DemoTimeline | null = null;
  // DM12 — dev-only "you are here" readout (camera · view · powered). Created in
  // the devMode block below; updated write-on-change in the render loop.
  let youAreHere: YouAreHere | null = null;
  // Guided first-run loop (story-flow.md) — demo-only; ticked in the render loop.
  let storyDirector: StoryDirector | null = null;
  // P1 #8 — reward-chrome interior camera toggle (orbit ⇄ walk); revealed on RETURN.
  let cameraModeToggle: CameraModeToggle | null = null;
  // Item 2 — bottom-context readout (time · era · cassette n/N); demo-only, hidden on reward.
  let bottomContext: BottomContext | null = null;
  // Item 6·A4 — "get out of the chair" pill; demo-only, shows only while seated.
  let getOutPill: GetOutPill | null = null;
  if (!devMode) {
    createDemoChip({ pointer });
    demoTimeline = createDemoTimeline({
      clock,
      morph: morphProgress,
      // PAST/PRESENT marker is a toggle: flip to the opposite pole, timed morph.
      toggleEra: () => stateController.setTarget(morphProgress() >= 0.5 ? 'past' : 'present'),
      // P0.5 — the strip is the REWARD chrome: hidden through the guided loop, the
      // story-director reveals it (+ enables scrub) on RETURN, not on the generator.
      revealOnPower: false,
    });
    // The guided first-run loop: a card-guided arc over the EXISTING primitives
    // (generator latch · view toggle · cassette clicks · era morph) that carries
    // the visitor through once, then completes into a reward chrome. It watches
    // those primitives — adds no new systems (story-flow.md).
    const storyCard = createStoryCard();
    // Item 4 — N = the count of DISTINCT, clickable cassette instances in the
    // scene (interactive `hero_cassette…` heroes, excluding the `#all`/`group:`
    // handles + the non-interactive case). Data-driven so fanning/adding tapes in
    // Blender just works. ⚑ John tunes WHICH cassettes count + the final N.
    let distinctCassetteCount = 0;
    for (const [hid, obj] of heroLookup) {
      if (!hid.startsWith('hero_cassette') || hid.endsWith('#all') || hid.startsWith('group:')) continue;
      if (isInteractive(obj)) distinctCassetteCount++;
    }
    storyDirector = createStoryDirector({
      card: storyCard,
      pointer,
      // n/N: every distinct clickable cassette must be heard to complete the loop.
      cassetteSetSize: distinctCassetteCount > 0 ? distinctCassetteCount : undefined,
      getView,
      currentEra: () => (morphProgress() >= 0.5 ? 'present' : 'past'),
      setEra: (era) => stateController.setTarget(era),
      fireNarration: (clipId) => soundscape.fireClipExternally(clipId),
      // P0.5 — loop complete (RETURN→SANDBOX): unlock the reward chrome — reveal
      // the demo timeline (era toggle) + make the day cycle scrubbable. P1 #9 —
      // and surface the free-play controls hint here (suppressed during the loop).
      onReward: () => { demoTimeline?.reveal(); demoTimeline?.setScrubbable(true); createControlsHint(); cameraModeToggle?.reveal(); bottomContext?.hide(); /* the reward chrome takes the bottom slot */ },
      // P1 #6 — the INSIDE-phase guidance pulse lights heroes via the existing
      // focus-glow (emissive boost, emit-free, its own snapshot stack).
      glowHero: (id) => hoverHighlight.focusRoot(id ? (heroLookup.get(id) ?? null) : null),
      // Item 3 (John, 2026-06-29: "make sure the SHOWING cassettes trigger the
      // past") — the INSIDE-phase "play a cassette" glow must land on a tape that's
      // actually visible + clickable right now. The old hardcoded hero_cassette_case
      // is past-only AND non-interactive, so in the present 'inside' beat it glowed
      // nothing and the visitor never found the trigger into the past. Pick the
      // first interactive, render-visible cassette instance (data-driven — fanning
      // tapes in Blender just works).
      pickShowingCassette: () => {
        for (const [hid, obj] of heroLookup) {
          if (!hid.startsWith('hero_cassette') || hid.includes('#all') || hid.startsWith('group:')) continue;
          if (!isInteractive(obj)) continue;
          let n: Object3D | null = obj, vis = true;
          while (n) { if (!n.visible) { vis = false; break; } n = n.parent; }
          if (vis) return hid;
        }
        return null;
      },
      // P1 #7 — the generator powers the shop: swell the room beds up from
      // silence (the ambient bus) rather than snapping on.
      roomFadeIn: (s) => audio.fadeInRoom(s),
      // Chair → sit and listen: tween into the seat, handing off to the locked-
      // freeform SEATED camera (anchored in the chair, look around — item 1). The
      // sit is AMBIENT now (John, 2026-06-29): no narration fires — Annour's lines
      // live on the cassette-wall click. Uses John's tuned 'chairnew' seat when
      // present (higher standpoint), else the original 'chair' bookmark.
      sitInChair: () => goToBookmark(bookmarks['chairnew'] ? 'chairnew' : 'chair', { handoff: 'locked-freeform' }),
      goInside: () => setView('interior'), // the entry moves to the action-slot "step inside" pill
    });
    // P1 #8 — reward-chrome interior camera toggle. orbit/walk are the two
    // curated interior rails; switching them keeps view='interior' (wall cull
    // stays correct). Only live inside (outside is freeform); revealed on RETURN.
    cameraModeToggle = createCameraModeToggle({
      getView,
      mode: () => view !== 'interior' ? null
        : activeCamera === cameras['interior-walk'] ? 'walk'
        : activeCamera === cameras['interior-orbit'] ? 'orbit' : null,
      setMode: (m) => { if (view === 'interior') setCamera(m === 'walk' ? 'interior-walk' : 'interior-orbit'); },
    });
    // Item 2 — the bottom-context readout: time · era · cassette n/N. Reads the
    // story director's distinct-cassette progress (item 4). Hidden on RETURN when
    // the reward chrome (demoTimeline scrub + era toggle) takes the bottom slot.
    bottomContext = createBottomContext({
      clock,
      currentEra: () => (morphProgress() >= 0.5 ? 'present' : 'past'),
      cassetteProgress: () => storyDirector?.cassetteProgress() ?? { played: 0, total: 0 },
    });
    // Item 6·A4 — the "get out of the chair" pill: shows only while seated
    // (LockedFreeform active), tweens back onto the interior orbit rail.
    getOutPill = createGetOutPill({
      isSeated: () => activeCamera === cameras['locked-freeform'],
      onGetOut: getOutOfChair,
    });
  }

  // Camera anchor markers (green = exterior pose, cyan = doorway) are
  // authoring aids — hide them on the demo view so directors don't see
  // floating dots in the scene.
  if (!devMode) {
    cameraHandles.exteriorMarker.visible = false;
    cameraHandles.doorwayMarker.visible = false;
  }

  if (devMode) {
    // Director mode plumbing — D hotkey + toggle pill + body-class CSS
    // that hides the legacy dev surfaces (lil-gui, hero state panel,
    // tracks bar, hero audio mixer) when director mode is active.
    // The new dev surfaces (gizmo dock, timeline, inspector, event log)
    // hide themselves via onDevModeChange subscriptions.
    initDirectorMode();

    // DM12 — the dev-only "you are here" readout: name the podium state (active
    // camera · view · powered) so the conductor can always answer "where am I?".
    // Demo view stays clean (never created there). Camera name = the key whose
    // mode is active in the cameras map.
    youAreHere = createYouAreHere({
      cameraName: () => Object.keys(cameras).find((k) => cameras[k] === getActiveCamera()) ?? 'camera',
      view: getView,
      powered: () => power.powered,
    });

    // lil-gui ("LEC · plumbing") is fully retired — every control it held now
    // lives in the inspector tabs + the timeline. (createDebugPanel deleted.)

    // Inspector — left-edge engine-style HUD with HEROES / AUDIO /
    // ATMOSPHERE / PERF tabs. Mirrors the lil-gui edit-hero dropdown
    // and audio folder in a discoverable, scrollable surface. Atmosphere
    // shows live palette swatches + tunables; Perf charts frame time +
    // renderer.info + subsystem counts. Refreshes its heroes list
    // whenever state tags change (same hook as the lil-gui dropdown).
    const inspector = createInspector({
      audio,
      heroLookup,
      heroIds: buildHeroDropdownMap(heroLookup),
      setEditTarget,
      getEditTarget,
      sunRig,
      atmospheres,
      getActiveAtmosphere,
      setAtmosphere,
      renderer,
      cloths,
      // dvScene is created in the same `if (devMode)` gate above, so it's
      // non-null here. The inspector's gizmo footer drives it.
      dv: dvScene!,
      // AUDIO tab palette: the full asset catalogue for the audition/arm chip
      // grid. (Hero beds moved to the timeline's AMBIENCE lane in Stage 2.)
      audioAssets: AUDIO_ASSETS,
      // U8: lets PLAYING NOW scope its stop/volume to non-engine handles.
      bedOwnedHandles: () => soundscape.bedOwnedHandles(),
      // Atmosphere shaft editing (absorbed from the lil-gui atmosphere folder).
      shaftHandleIds,
      setShaftEditTarget,
      // DM6 — the per-aspect save now routes through the hub (one machine): it
      // shares the dirty/conflict state save-on-commit uses, so the button and an
      // auto-save can't disagree. No more gizmoUndo.clear() — under save-on-commit
      // an undo re-commits the reverted state, so the undo stack never crosses a
      // stale save boundary (DM1's trap is dissolved, per the DM6 design note).
      saveShaftConfig: async () => (await saveHub.flush('atmosphere')) === 'saved',
      snapToShaftRef: () => { clock.t = morningShaft.shaftReferenceT; },
      // Hero editing (absorbed from the lil-gui edit folder).
      getGizmoMode,
      setGizmoMode,
      setHeroScale,
      // DM4 — retag rides the shared gizmo-undo stack (one recency-arbitrated
      // timeline) so ⌘Z reverts a tag change like any other edit. DM6 — tag it
      // 'manifest' so the undo/redo re-commits to manifest.json (a retag changes
      // the saved `state`), and the push itself auto-persists.
      pushUndo: (u, r) => gizmoUndo.pushCommand(u, r, 'manifest'),
      // DM6 — manifest's per-aspect button routes through the hub (one machine,
      // shared dirty/conflict state with save-on-commit). Returns SaveResult so
      // the inspector still paints saved ✓ / disk ✕ / save ✕ (DM5). The DM1
      // clear-on-save is gone: save-on-commit makes an undo re-commit the
      // reverted state, so the undo stack never disagrees with disk.
      saveHeroPositions: async () => saveHub.flush('manifest'),
      // Camera (absorbed from the lil-gui camera folder).
      cameras,
      getActiveCamera,
      setCamera,
      cullSettings,
      bookmarks,
      goToBookmark,
      saveCurrentAsBookmark: (name: string) => {
        const p = saves.saveCurrentAsBookmark(name);
        devBus.emit('bookmark:saved', { name });
        return p;
      },
      // DM13 — the "frame this hero" offer: a hero-anchored bookmark.
      saveHeroAnchoredBookmark: (heroId: string, name: string) => {
        const p = saves.saveHeroAnchoredBookmark(heroId, name);
        devBus.emit('bookmark:saved', { name });
        return p;
      },
      deleteBookmark: (name: string) => saves.deleteBookmark(name),
      saveCameraPose: () => saves.saveCameraPose(),
      saveCurrentAsDoorway: () => saves.saveCurrentAsDoorway(),
      // Fired when the HEROES tab retags a hero (past/present/both) — the
      // absorbed hero-state-panel behaviour. Re-cache the transition's mesh
      // list against the new tags so visibility flips immediately, and
      // refresh the dropdown labels everywhere.
      onHeroRetag: () => {
        active.dispose();
        active.init(scene);
        const newMap = buildHeroDropdownMap(heroLookup);
        inspector.refreshHeroes(newMap);
      },
    });

    // Hover label: a floating chip on whatever hero is under the cursor —
    // enriched from the bare hero_id to "hero · era · ♪ what's sounding here",
    // so a 3D hover answers "what am I looking at + hearing" at a glance (era
    // from the morph, sounds from the live audio anchored to this hero).
    createHoverLabel({
      pointer,
      domElement: renderer.domElement,
      formatLabel: ({ heroId }) => {
        if (!heroId) return '';
        const short = heroId.replace(/^hero_/, '');
        const era = morphProgress() >= 0.5 ? 'present' : 'past';
        const here = audio.listPlaying().filter((p) => {
          const o = audio.getHandleObject(p.handle);
          return o ? getHeroId(o) === heroId : false;
        });
        const sounds = here.map((p) => p.id.replace(/^sfx_/, '').replace(/^test_music_/, 'music ')).join(', ');
        return `${short} · ${era}` + (sounds ? ` · ♪ ${sounds}` : '');
      },
    });
  }

  window.addEventListener('resize', () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  });

  const updateBoomboxEmissive = (heroId: string, on: boolean): void => {
    const obj = heroLookup.get(heroId);
    if (!obj) return;
    const mesh = obj as Mesh;
    if (!mesh.isMesh) return;
    const mat = mesh.material as MeshStandardMaterial;
    mat.emissiveIntensity = on ? 0.6 : 0;
  };

  // Debug hook for runtime inspection. Exposed surfaces let you poke at
  // every subsystem from DevTools without re-wiring buttons:
  //   __lec.heroLookup.get('hero_boombox')
  //   __lec.morningShaft.getCurrentConfig()
  //   __lec.audio.listPlaying()
  //   __lec.activeCamera, __lec.activeAtmosphere
  //   __lec.gizmoUndo.undo() / .redo()
  (window as Window & { __lec?: unknown }).__lec = {
    scene,
    camera,
    renderer,
    heroLookup,
    resolveHeroObject, // item 8 — bare hero id → era-visible instance (the audio/interaction anchor resolver)
    pointer, // interaction raycaster — drive/inspect picking (item 7 WYSIWYG gate)
    tc,
    get gizmoTarget() { return getGizmoTarget(); },
    // Selection entry points for the dev console + agent driving (the director
    // lane's standing __lec ask): select a hero (attaches the gizmo) or read the
    // current edit target. Used to exercise the DM2 detach + DM7/DM8 paths.
    setEditTarget,
    getEditTarget,
    audio,
    atmospheres,
    morningShaft,
    cameras,
    bookmarks,
    gizmoUndo,
    saveHub, // DM6 — saveAll() / flush(id) / setAutoSave(bool) / globalVis() — save-on-commit + ⌘S
    clock,
    soundscape,
    power,
    storyDirector, // demo-only guided first-run loop (null under ?dev=1) — .phase / .update(ms)
    get bottomContext() { return bottomContext; }, // item 2 — bottom readout (demo-only) — .update()/.hide()
    get getOutPill() { return getOutPill; }, // item 6·A4 — seated get-out pill (demo-only) — .update()
    getOutOfChair, // item 6·A4 — leave the chair → tween back to the interior rail
    audioCues,
    placements: scenePlacements,
    cloths, // the live ClothPatch instances (grab/pin state pokeable from DevTools)
    stateController,
    sunRig,
    cloudSky,
    get activeCamera() { return activeCamera; },
    get activeAtmosphere() { return activeAtmosphere; },
  };

  // Fade out the index.html loading overlay now that the scene is wired
  // and the first render is about to happen. CSS handles the transition;
  // we just toggle the class.
  const loadingOverlay = document.getElementById('lec-loading');
  if (loadingOverlay) {
    loadingOverlay.classList.add('hidden');
    // Remove from the DOM after the CSS transition so it can't catch input.
    setTimeout(() => loadingOverlay.remove(), 800);
  }

  let prev = performance.now();
  const tick = (now: number): void => {
    const dt = (now - prev) / 1000;
    prev = now;
    stateController.tick(dt);
    active.update(stateController.context);
    activeCamera.update(dt);

    // Time-of-day + lighting unification. Order matters:
    //   1. clock.tick advances t if running.
    //   2. sunRig.update recomputes sun direction + palette + DirectionalLight
    //      + AmbientLight from the new t. Atmospheres downstream see the
    //      fresh state via ctx.sunRig.
    //   3. cloudSky reads sunRig + re-centres on the camera (sphere tracks
    //      camera so the sky stays at infinity).
    //   4. atmosphere update (MorningShaft) reads sunRig, rotates its
    //      cones + scales intensity by altitude.
    //   5. Fog colour mirrors palette bottom so the horizon line of the
    //      cloud sky meets the fog softly. Works for both Fog and FogExp2.
    clock.tick(dt);
    soundscape.update(clock.t, clock.running);
    demoTimeline?.update();
    storyDirector?.update(dt * 1000); // guided first-run watchdog + view-poll (demo only)
    bottomContext?.update(); // item 2 — time · era · cassette n/N readout (write-on-change, demo only)
    getOutPill?.update(); // item 6·A4 — show the "get out" pill only while seated (demo only)
    cameraModeToggle?.update(); // reward-chrome camera toggle: enable/highlight by view + mode (demo only)
    youAreHere?.update(); // DM12 — write-on-change podium readout (dev only)
    sunRig.update();
    // #8 — power-ramped pre-generator dim (demo only). sunRig.update() just wrote
    // the time-of-day intensities; scale them by the ramp so the dark shop swells
    // to full light when the generator wakes it.
    if (!devMode) {
      const dimTarget = power.powered ? 1 : PRE_GEN_DIM;
      preGenDim += (dimTarget - preGenDim) * Math.min(1, dt * 1.8);
      sunRig.directional.intensity *= preGenDim;
      sunRig.ambient.intensity *= preGenDim;
    }
    cloudSky.update(camera.position, dt);
    activeAtmosphere.update(atmosphereCtx, dt);
    if (scene.fog) scene.fog.color.copy(sunRig.palette.bottom);
    // Item 7 — carry the cloths along if the table was repositioned (gizmo).
    if (cloths.length > 0) {
      const tbl = heroLookup.get('hero_table');
      if (tbl) {
        tbl.updateWorldMatrix(true, false);
        tbl.getWorldPosition(clothFollowScratch);
        const dx = clothFollowScratch.x - clothTablePrevPos.x;
        const dy = clothFollowScratch.y - clothTablePrevPos.y;
        const dz = clothFollowScratch.z - clothTablePrevPos.z;
        if (dx * dx + dy * dy + dz * dz > 1e-10) {
          for (const cloth of cloths) cloth.translate(dx, dy, dz);
          clothTablePrevPos.copy(clothFollowScratch);
        }
      }
    }
    for (const cloth of cloths) cloth.tick(dt);
    // Mirror any gizmo-driven marker changes into the source-of-truth
    // vectors that the tween + save flow read.
    cameraHandles.syncToSources();
    // Run cull AFTER transition update — wall cull is the final say on
    // outside-mesh visibility regardless of state opacity.
    wallCull.update();
    audio.syncSpatial(camera);

    // Dev viz refresh — each gizmo system early-returns if its group is
    // hidden, so total cost is near-zero when gizmos are off. Null in the
    // demo/viewer view (dev surfaces aren't created without ?dev).
    audioGizmos?.update();
    clothGizmos?.update();
    sceneGizmos?.update();

    // Boombox glows while music plays. Only the past placement exists
    // today; when a present-state boombox lands, extend this loop.
    updateBoomboxEmissive('hero_boombox', audio.isChannelActive('music'));

    renderer.render(scene, camera);
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}
